/*
  Scroll fade — home page and product pages.
  Text and images in every section of #MainContent fade (and drift) into view the
  first time they scroll into the viewport, and then stay: scrolling back up never
  fades anything out. Every scroll down plays the fade-in again, as on the first
  visit: scrolling up, a block that has fully left the screen through the bottom
  is quietly reset (it is off screen, so nothing is seen disappearing) and fades
  in again when it comes back up from below.

  - Elements are grouped into "units": the smallest block (card, image + text
    row, ...) that holds both an image and text, as long as it fits on screen.
    Everything in a unit fades together, so text laid over or next to an image
    never shows up after it. Elements outside such a block are their own unit.
  - A banner (text over a background image) is one unit at any height, so text at
    the bottom of a tall banner (e.g. on ultra-wide screens) comes in with it.
  - A slider / horizontal carousel is one unit, so swiping never fades slides in.
  - Frames fade with what they hold: an image or text inside a card, circle or
    pill that paints its own box (background, border or shadow) animates as that
    whole box, so the frame never shows up before its image / icon.
  - Prev / next arrow buttons never fade: they are there as soon as the section is.
  - Fixed elements (sticky add-to-cart bars, modals) are left alone.
  - Uses the Web Animations API (opacity + the individual `translate` property),
    so it never overrides a section's own `transition` / `transform` / opacity
    rules: once an element is shown, the animation ends and the section's CSS
    is back in charge.
  - Elements already on screen when the page loads stay as they are (no flash
    on the hero).
  - `data-scroll-fade="group"` on a wrapper fades it as one element (used for the
    logo marquee, whose logos move across the edges on their own).
  - `data-scroll-fade="off"` on a wrapper opts its content out.
  - Product pages: the first section with the purchase form (main product /
    product hero) is left alone and shows normally.
  - Content starts to appear as soon as it comes onto the screen (once it is
    10% of the screen height in), so nothing waits for a scroll to show up.
  - Content above the screen at load (e.g. the browser restored the scroll position
    on reload) fades in when first scrolled up to, a little below the header (which
    slides back in on scroll-up), so its fade-in is seen instead of happening
    behind the header.
  - Photos never come in after their frame: images start downloading well before
    their block reaches the screen, and a block whose photos are still loading
    waits for them (up to WAIT_MS) so frame and photo appear in the same moment.
  - Inline SVG icons count as images, so an icon fades with its circle / text.
  - Text that goes with a photo comes in with it: when a photo fades in, the text
    right under it (or right above it, when scrolling back up) in the same section
    fades in at the same moment, even if the photo + text together are taller than
    the screen (stacked mobile layouts) and so are not one unit.
  - Wrappers with `display: contents` (used to restack columns on mobile) have no
    box of their own, so they are never picked as the block that fades.
  - Fade-in: 0.5s while rising 20px (also with prefers-reduced-motion; the
    rise is small and slow enough to be comfortable).
*/
(function () {
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
  var page = document.currentScript ? document.currentScript.getAttribute('data-page') : '';

  var ROOT_SELECTOR = '#MainContent';
  var MEDIA = 'img, svg, video, iframe, [data-scroll-fade="group"]';
  var TEXT = 'h1, h2, h3, h4, h5, h6, p, blockquote, ' +
             'a[class*="btn"], a[class*="button"], button[class*="btn"], button[class*="button"]';
  var TARGETS = MEDIA + ', ' + TEXT;

  var DISTANCE = 20;              /* px the element rises while fading in */
  var SHOW_MS = 500;
  var REVEAL_AT = 0.9;            /* reveal once the top is 10% of the screen height in */
  var PRELOAD = '150%';           /* start loading a block's photos 1.5 screens before it arrives */
  var WAIT_MS = 800;              /* longest a block waits for its photos before showing anyway */
  var TOP_GAP = 0.05;             /* coming in over the top: reveal 5% of the screen height below the header */
  var MAX_UNIT = 0.9;             /* a unit can be at most 90% of the screen height */
  var LINK_GAP = 0.25;            /* text this close to a photo (share of the screen height) comes in with it */
  var PHOTO = 'img, video, iframe, [data-scroll-fade="group"]';
  var EASE_OUT = 'cubic-bezier(0.25, 0.1, 0.25, 1)';   /* soft start, long gentle settle */

  var anims = new WeakMap();      /* element -> { hideAnim } */
  var units = new Map();          /* unit element -> { node, members, hidden, ready } */

  function offset(fromAbove) {
    return '0 ' + (fromAbove ? -DISTANCE : DISTANCE) + 'px';
  }

  /* Hidden until revealed: always set instantly while off screen, never animated out. */
  function hideEl(el, fromAbove) {
    anims.get(el).hideAnim = el.animate([{ opacity: 0, translate: offset(fromAbove) }], { duration: 0, fill: 'forwards' });
  }

  function showEl(el, fromAbove) {
    var a = anims.get(el);
    if (a.hideAnim) { a.hideAnim.cancel(); a.hideAnim = null; }
    el.animate([{ offset: 0, opacity: 0, translate: offset(fromAbove) }], { duration: SHOW_MS, easing: EASE_OUT });
  }

  function setHidden(unit, hidden, fromAbove) {
    if (unit.hidden === hidden) return;
    unit.hidden = hidden;
    unit.members.forEach(function (el) {
      if (hidden) hideEl(el, fromAbove); else showEl(el, fromAbove);
    });
    /* Its photos are in: no need to preload them again. */
    if (!hidden) done(unit);
  }

  function done(unit) {
    preloader.unobserve(unit.node);
  }

  /* Photos of a block that are laid out (not display: none, e.g. the mobile image
     on desktop) and not downloaded yet. */
  function pendingImages(node) {
    var list = node.tagName === 'IMG' ? [node] : Array.prototype.slice.call(node.querySelectorAll('img'));
    return list.filter(function (img) {
      return !img.complete && img.getClientRects().length > 0;
    });
  }

  function loadNow(img) {
    if (img.loading === 'lazy') img.loading = 'eager';
    if (window.lazySizes && img.classList.contains('lazyload')) window.lazySizes.loader.unveil(img);
  }

  function hasPhoto(unit) {
    if (unit.photo === undefined) {
      unit.photo = unit.members.some(function (el) { return el.matches(PHOTO) || !!el.querySelector(PHOTO); });
    }
    return unit.photo;
  }

  /* The text that goes with a photo: the run of text-only blocks right under it
     (below = true) or right above it in the same section, each within LINK_GAP of
     the screen height from the one before. Stops at the next photo. */
  function linkedText(unit, node, below) {
    if (!unit.section || !hasPhoto(unit)) return [];
    var r = node.getBoundingClientRect();
    if (!r.height) return [];
    var slack = DISTANCE + 2;     /* either side may still be shifted by a fade */
    var list = [];
    units.forEach(function (other, otherNode) {
      if (other === unit || other.section !== unit.section) return;
      var o = otherNode.getBoundingClientRect();
      if (!o.height || o.right <= r.left || o.left >= r.right) return;
      if (below ? o.top >= r.bottom - slack : o.bottom <= r.top + slack) list.push({ unit: other, rect: o });
    });
    list.sort(function (a, b) { return below ? a.rect.top - b.rect.top : b.rect.bottom - a.rect.bottom; });
    var gap = window.innerHeight * LINK_GAP;
    var edge = below ? r.bottom : r.top;
    var chain = [];
    for (var i = 0; i < list.length; i++) {
      var o = list[i].rect;
      if (hasPhoto(list[i].unit) || (below ? o.top - edge : edge - o.bottom) > gap) break;
      chain.push(list[i].unit);
      edge = below ? Math.max(edge, o.bottom) : Math.min(edge, o.top);
    }
    return chain;
  }

  /* Show a unit once its photos are in, so the frame never shows up before the photo.
     The text that goes with it (under it when it comes up from below, above it when
     it comes down from the top) is shown in the same moment. */
  function reveal(unit, node, fromAbove) {
    if (!unit.hidden || unit.waiting) return;
    var text = linkedText(unit, node, !fromAbove);
    function show() {
      setHidden(unit, false, fromAbove);
      text.forEach(function (t) { if (!t.waiting) setHidden(t, false, fromAbove); });
    }
    var imgs = pendingImages(node);
    if (!imgs.length) { show(); return; }
    unit.waiting = true;
    var left = imgs.length;
    var timer = setTimeout(go, WAIT_MS);
    function go() {
      if (!unit.waiting) return;
      unit.waiting = false;
      clearTimeout(timer);
      /* Still on screen? (It may have been scrolled away while its photos loaded.) */
      var r = node.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) show();
    }
    function done() { if (--left <= 0) go(); }
    imgs.forEach(function (img) {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      loadNow(img);
    });
  }

  /* Download photos well before their block gets to the screen. */
  var preloader = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      preloader.unobserve(entry.target);
      pendingImages(entry.target).forEach(loadNow);
    });
  }, { rootMargin: '0px 0px ' + PRELOAD + ' 0px' });

  /* The reveal line at the top: the bottom of the header (shown again on scroll-up)
     plus TOP_GAP. A header taller than a third of the screen is ignored. */
  function topInset() {
    var h = 0;
    ['shopify-section-header', 'nav-header'].forEach(function (id) {
      var n = document.getElementById(id);
      if (n) h = Math.max(h, n.offsetHeight);
    });
    if (h > window.innerHeight / 3) h = 0;
    return Math.round(h + window.innerHeight * TOP_GAP);
  }

  var thresholds = [];
  for (var t = 0; t <= 20; t++) thresholds.push(t / 20);

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var unit = units.get(entry.target);
      if (!unit) return;
      var rect = entry.boundingClientRect;
      var root = entry.rootBounds || { top: 0, height: window.innerHeight };
      var above = rect.top + rect.height / 2 < root.top + root.height / 2;
      var seen = entry.isIntersecting ? entry.intersectionRect.height : 0;

      var last = unit.seen;
      unit.seen = seen;
      if (!unit.ready) {
        unit.ready = true;
        /* First report: whatever is off screen at load starts hidden, without animating
           (judged on the whole screen, so nothing visible at load ever disappears). */
        var onScreen = rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
        if (onScreen) done(unit);
        else setHidden(unit, true, above);
        return;
      }
      /* In once it has come up to the reveal line; after that it stays (until it is
         reset off screen below, see resetObserver). */
      if (unit.hidden && seen > last) reveal(unit, entry.target, above);
    });
  }, { threshold: thresholds, rootMargin: '-' + topInset() + 'px 0px -' + Math.round((1 - REVEAL_AT) * 100) + '% 0px' });

  /* Scrolling up: a shown block that has fully left the screen through the bottom is
     hidden again (instantly - it is off screen), so the next scroll down fades it in
     again. So is the text shown with it that is still below the screen. A block
     that left over the top stays as it is: nothing on screen ever disappears. */
  var resetObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var unit = units.get(entry.target);
      if (!unit || entry.isIntersecting || unit.hidden || unit.waiting) return;
      var bottom = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
      if (entry.boundingClientRect.top < bottom - 1) return;
      var text = linkedText(unit, entry.target, true);
      setHidden(unit, true, false);
      text.forEach(function (t) {
        if (!t.hidden && !t.waiting && t.node.getBoundingClientRect().top >= window.innerHeight) setHidden(t, true, false);
      });
    });
  });

  /* At the very bottom of the page content can't scroll up to the reveal line,
     so whatever is still hidden on screen there is shown. */
  var bottomQueued = false;
  function revealAtBottom() {
    bottomQueued = false;
    if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 2) return;
    units.forEach(function (unit, node) {
      if (!unit.hidden) return;
      var r = node.getBoundingClientRect();
      if (r.height > 0 && r.bottom > 0 && r.top < window.innerHeight) reveal(unit, node, false);
    });
  }
  window.addEventListener('scroll', function () {
    if (!bottomQueued) { bottomQueued = true; requestAnimationFrame(revealAtBottom); }
  }, { passive: true });

  /* A slider / horizontally scrolling track: its slides fade in together, so the
     off-screen ones are already visible when they are swiped or dragged in. */
  var CAROUSEL_CLASSES = ['swiper', 'swiper-container', 'slick-list', 'flickity-viewport', 'splide__track'];
  function isCarousel(node) {
    for (var i = 0; i < CAROUSEL_CLASSES.length; i++) {
      if (node.classList.contains(CAROUSEL_CLASSES[i])) return true;
    }
    if (node.scrollWidth <= node.clientWidth + 1) return false;
    var x = getComputedStyle(node).overflowX;
    return x === 'auto' || x === 'scroll' || x === 'hidden' || x === 'clip';
  }

  /* A banner: a block whose background is an image (or video) covering all of it. */
  var backdrops = new WeakMap();
  function hasBackdrop(node, box) {
    if (backdrops.has(node)) return backdrops.get(node);
    var found = false;
    if (box.width && box.height) {
      var media = node.querySelectorAll('img, video');
      for (var i = 0; i < media.length && !found; i++) {
        var r = media[i].getBoundingClientRect();
        found = r.width >= box.width * 0.9 && r.height >= box.height * 0.9;
      }
    }
    backdrops.set(node, found);
    return found;
  }

  /* The block `el` fades with: the carousel it sits in; else the banner it sits in
     (text over a background image, at any height, so text at the bottom of a tall
     banner comes in with it); else the smallest block that holds both an image and
     text and fits on screen; else the element itself. */
  function unitFor(el, root) {
    var maxHeight = window.innerHeight * MAX_UNIT;
    var unit = null;
    var tall = false;             /* past a block taller than the screen: only a banner can still be the unit */
    for (var node = el.parentElement; node && node !== root; node = node.parentElement) {
      if (node.classList.contains('shopify-section')) break;
      if (getComputedStyle(node).display === 'contents') continue;   /* no box of its own */
      var box = node.getBoundingClientRect();
      if (box.height > window.innerHeight) tall = true;
      if (!tall && isCarousel(node)) return node;
      if (node.querySelector(MEDIA) && node.querySelector(TEXT)) {
        if (hasBackdrop(node, box)) unit = node;
        else if (!unit && !tall && box.height <= maxHeight) unit = node;
      }
    }
    return unit || el;
  }

  /* Fixed bars and pop-ups (sticky add-to-cart, modals) have their own show / hide. */
  function inFixed(el, root) {
    for (var node = el; node && node !== root; node = node.parentElement) {
      if (getComputedStyle(node).position === 'fixed') return true;
    }
    return false;
  }

  /* Product pages: the first section holding the purchase form shows as normal. */
  function purchaseSection(root) {
    if (page !== 'product') return null;
    var form = root.querySelector('#ProductForm, form[action*="/cart/add"]');
    return form ? form.closest('.shopify-section') || form : null;
  }

  /* Prev / next arrows of sliders: shown as soon as the section is, never faded. */
  var NAV_NAME = /(^|[\s_-])(prev|previous|next|arrow|nav)/i;
  var BUTTONS = 'button, [role="button"], [class*="swiper-button"]';
  function isNavButton(btn) {
    return NAV_NAME.test(btn.getAttribute('class') || '') || NAV_NAME.test(btn.getAttribute('aria-label') || '');
  }
  function isNav(el, root) {
    var btn = el.closest(BUTTONS);
    return !!btn && root.contains(btn) && isNavButton(btn);
  }

  function visibleColor(c) {
    return !!c && c !== 'transparent' && !/rgba?\([^)]*,\s*0\)$/.test(c);
  }

  /* Does the node paint a box of its own (card, circle, pill, framed image)?
     A background counts when its corners are rounded (cards, circles - at any width,
     cards are near full width on phones) or it is not a full-width band (square
     full-width backgrounds are section backgrounds). */
  function paintsBox(node, box) {
    var cs = getComputedStyle(node);
    if (cs.boxShadow && cs.boxShadow !== 'none') return true;
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    for (var i = 0; i < 4; i++) {
      if (parseFloat(cs['border' + sides[i] + 'Width']) > 0 &&
          cs['border' + sides[i] + 'Style'] !== 'none' &&
          visibleColor(cs['border' + sides[i] + 'Color'])) return true;
    }
    var bg = cs.backgroundImage !== 'none' || visibleColor(cs.backgroundColor);
    if (!bg) return false;
    return parseFloat(cs.borderTopLeftRadius) > 0 || parseFloat(cs.borderBottomRightRadius) > 0 ||
           box.width < window.innerWidth * 0.95;
  }

  /* What actually animates for `el`: the outermost box around it that paints a
     frame (so the frame and its image / icon / text come in as one piece), up to
     the section, a carousel track, a block taller than MAX_UNIT of the screen, or a
     block that also holds slider arrows (those must not fade with it). */
  function boxFor(el, root, navs) {
    var result = el;
    var maxHeight = window.innerHeight * MAX_UNIT;
    for (var node = el.parentElement; node && node !== root; node = node.parentElement) {
      if (node.classList.contains('shopify-section') || node.hasAttribute('data-scroll-fade')) break;
      if (isCarousel(node)) break;
      if (navs.some(function (nav) { return node.contains(nav); })) break;
      if (getComputedStyle(node).display === 'contents') continue;   /* no box of its own */
      var box = node.getBoundingClientRect();
      if (box.height > maxHeight) break;
      if (paintsBox(node, box)) result = node;
    }
    return result;
  }

  function scan(scope) {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    var skip = purchaseSection(root);
    var boxes = [];
    var picked = new Set();
    var navs = Array.prototype.filter.call(root.querySelectorAll(BUTTONS), isNavButton);
    (scope || root).querySelectorAll(TARGETS).forEach(function (el) {
      if (el.closest('[data-scroll-fade="off"]')) return;
      if (skip && skip.contains(el)) return;
      if (isNav(el, root)) return;
      if (inFixed(el, root)) return;
      var box = boxFor(el, root, navs);
      if (!picked.has(box)) { picked.add(box); boxes.push(box); }
    });

    boxes.forEach(function (el) {
      if (anims.has(el)) return;
      /* Only the outermost box animates (e.g. an <img> inside a button, card or group moves with it). */
      for (var p = el.parentElement; p && p !== root; p = p.parentElement) {
        if (picked.has(p) || anims.has(p)) return;
      }
      anims.set(el, { hideAnim: null });

      var node = unitFor(el, root);
      var unit = units.get(node);
      if (!unit) {
        unit = { node: node, members: [], hidden: false, ready: false, seen: 0, waiting: false,
                 section: node.closest('.shopify-section') };
        units.set(node, unit);
        observer.observe(node);
        preloader.observe(node);
        resetObserver.observe(node);
      } else if (unit.hidden) {
        hideEl(el, false);   /* joins a unit that is already hidden */
      }
      unit.members.push(el);
    });
  }

  function init() { scan(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Theme editor: pick up sections that are added or re-rendered. */
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
